import { useNavigate } from "react-router-dom";
import { UpgradePrompt } from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import { useApi } from "../hooks/useApi.js";
import { AsyncBoundary } from "../ui/Feedback.jsx";

/**
 * UpgradePrompt's usage numbers come from `GET /billing` (the only contract
 * endpoint that carries used/total feeds). The Enterprise "Talk to us" button has
 * no endpoint: the destination rides along on the billing response as
 * `contact_url`, and the constant below is only the fallback for a server that
 * predates it — a real, staffed address rather than a placeholder, because a
 * deployment that shows it is one where the server could not supply its own.
 *
 * Choosing a tier here does not start a payment: it opens the period screen,
 * where the month or the year is chosen, and only then is a checkout created for
 * that price. A prompt that bought a month on the spot would decide the period on
 * the customer's behalf.
 */
const SUPPORT_EMAIL = "support@yourcals.com";

export default function UpgradeScreen() {
  const api = useApiClient();
  const navigate = useNavigate();
  const query = useApi(() => api.billing.get(), [api]);

  return (
    <div>
      <AsyncBoundary query={query} onBack={() => navigate("/")}>
        {(data) => (
          <UpgradePrompt
            usedFeeds={data.plan?.usedFeeds ?? 0}
            totalFeeds={data.plan?.totalFeeds ?? 0}
            // Every tier this deployment can charge for travels with the same
            // response as the usage numbers, in catalogue order, so the prompt
            // offers the whole catalogue and quotes its prices instead of naming
            // one plan and describing it from memory.
            tiers={(data.plans ?? []).filter((plan) => plan.purchasable && !plan.contactOnly)}
            onUpgrade={(planId) => navigate(`/billing/plan/${planId}`)}
            onContactSales={() => {
              // The server owns the destination so the screen never hardcodes an
              // address; a mailto: without the subject still reaches a person.
              window.location.assign(
                data.contactUrl ||
                  `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Enterprise plan enquiry")}`,
              );
            }}
            onRemoveFeed={() => navigate("/")}
          />
        )}
      </AsyncBoundary>
    </div>
  );
}
